import React from 'react';
import { format } from 'date-fns';
import { Combobox } from '@headlessui/react';
import { useRouter } from 'next/router';
import { BiSearch } from 'react-icons/bi';
import { IoMdSunny, IoMdMoon, IoMdClock } from 'react-icons/io';
import { MdInfoOutline } from 'react-icons/md';
import { DatePicker, DatePickerInput } from 'carbon-components-react';
import classNames from 'classnames';
import { getCalendarPage } from 'constants/pages';
import { TickerType } from '../types';
import { searchTickerFilter } from 'utils/shared/tickers/searchTickerFilter';

const timeframeOptions = ['Last 7 Days', 'Yesterday', 'Today', 'Tomorrow', 'Next 7 Days'] as const;

type Timeframe = (typeof timeframeOptions)[number];

// Base filter option type
interface FilterOption {
  label: React.ReactNode;
  value: string;
}

// Filter configuration type
interface FilterConfig {
  hasTickerSearch: boolean;
  thirdSectionTitle: string;
  thirdSectionOptions: FilterOption[];
  fourthSectionTitle?: string;
  fourthSectionOptions?: FilterOption[];
  tooltipContent?: React.ReactNode;
  getTooltipTitle?: (value: string) => string;
}

export const FILTER_CONFIGS: Record<string, FilterConfig> = {
  earnings: {
    hasTickerSearch: true,
    thirdSectionTitle: 'Time',
    thirdSectionOptions: [
      { label: 'All', value: 'all' },
      { label: <IoMdSunny className="text-lg text-yellow-400" />, value: 'BM' },
      { label: <IoMdClock className="text-lg" />, value: 'DM' },
      { label: <IoMdMoon className="text-lg text-blue-400" />, value: 'AM' },
    ],
    tooltipContent: (
      <div className="space-y-2">
        <p className="flex items-center gap-2">
          <IoMdSunny className="flex-shrink-0 text-sm text-yellow-400" />
          <span>Before Market Opens</span>
        </p>
        <p className="flex items-center gap-2">
          <IoMdClock className="flex-shrink-0 text-sm text-white" />
          <span>During Market Hours</span>
        </p>
        <p className="flex items-center gap-2">
          <IoMdMoon className="flex-shrink-0 text-sm text-blue-400" />
          <span>After Market Close</span>
        </p>
      </div>
    ),
    getTooltipTitle: (value: string) => {
      const tooltips: { [key: string]: string } = {
        all: 'All Times',
        BM: 'Before Market',
        DM: 'During Market',
        AM: 'After Market',
      };
      return tooltips[value] || '';
    },
    fourthSectionTitle: 'Analyst Ratings',
    fourthSectionOptions: [
      { label: 'All', value: 'all' },
      { label: 'Buy', value: 'Buy' },
      { label: 'Hold', value: 'Hold' },
      { label: 'Sell', value: 'Sell' },
    ],
  },
  'economic-events': {
    hasTickerSearch: false,
    thirdSectionTitle: 'Currency',
    thirdSectionOptions: [
      { label: 'All', value: 'all' },
      { label: 'USD', value: 'USD' },
      { label: 'EUR', value: 'EUR' },
      { label: 'JPY', value: 'JPY' },
      { label: 'GBP', value: 'GBP' },
      { label: 'CNY', value: 'CNY' },
      { label: 'AUD', value: 'AUD' },
      { label: 'CAD', value: 'CAD' },
    ],
    fourthSectionTitle: 'Impact',
    fourthSectionOptions: [
      { label: 'All', value: 'all' },
      { label: 'High', value: 'High' },
      { label: 'Medium', value: 'Medium' },
      { label: 'Low', value: 'Low' },
    ],
  },
  ipos: {
    hasTickerSearch: false,
    thirdSectionTitle: 'Status',
    thirdSectionOptions: [
      { label: 'All', value: 'all' },
      { label: 'Expected', value: 'Expected' },
      { label: 'Priced', value: 'Priced' },
    ],
  },
  dividends: {
    hasTickerSearch: true,
    thirdSectionTitle: 'Frequency',
    thirdSectionOptions: [
      { label: 'All', value: 'all' },
      { label: 'Annual', value: 'Annual' },
      { label: 'Semi-Annual', value: 'Semi-Annual' },
      { label: 'Quarterly', value: 'Quarterly' },
      { label: 'Monthly', value: 'Monthly' },
      { label: 'Other', value: 'Irregular' },
    ],
  },
  splits: {
    hasTickerSearch: true,
    thirdSectionTitle: 'Type',
    thirdSectionOptions: [
      { label: 'All', value: 'all' },
      { label: 'Forward', value: 'Forward' },
      { label: 'Reverse', value: 'Reverse' },
    ],
  },
};

interface FilterPanelProps {
  screenType: keyof typeof FILTER_CONFIGS;
  selectedTicker?: TickerType | null;
  setSelectedTicker?: (val: TickerType | null) => void;
  tickerData?: TickerType[];
  searchResults?: TickerType[];
  setSearchResults?: (val: TickerType[]) => void;

  // Date props
  dateValue: string;
  selectedDate: Date | null;
  handleDateChange: (event: any) => void;
  clearDateSelection: () => void;

  // Timeframe props
  selectedTimeframe: Timeframe;
  setSelectedTimeframe: (val: Timeframe) => void;

  // Third section filter props
  selectedThirdSectionFilters: Set<string>;
  toggleThirdSectionFilter: (val: string) => void;

  // Fourth section filter props (for economic events)
  selectedFourthSectionFilters?: Set<string>;
  toggleFourthSectionFilter?: (val: string) => void;

  // Additional props for date range functionality
  fromDate?: Date | null;
  toDate?: Date | null;
  formatDateForAPI?: (date: Date) => string;

  // Results count for table header
  resultsCount?: number;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  screenType,
  selectedTicker,
  setSelectedTicker,
  tickerData = [],
  searchResults = [],
  setSearchResults,
  dateValue,
  selectedDate,
  handleDateChange,
  clearDateSelection,
  selectedTimeframe,
  setSelectedTimeframe,
  selectedThirdSectionFilters,
  toggleThirdSectionFilter,
  selectedFourthSectionFilters,
  toggleFourthSectionFilter,
  fromDate,
  toDate,
  formatDateForAPI,
  resultsCount,
}) => {
  const router = useRouter();
  const config: FilterConfig = FILTER_CONFIGS[screenType];

  const search = (query: string) => {
    if (query.length >= 1 && setSearchResults) {
      const results = searchTickerFilter(query, tickerData);
      setSearchResults(results);
    } else if (setSearchResults) {
      setSearchResults([]);
    }
  };

  const renderTickerSearch = () => {
    if (!config.hasTickerSearch || !setSelectedTicker || !setSearchResults) return null;

    return (
      <div className="w-full flex-shrink-0 sm:w-auto sm:min-w-[250px]">
        <label className="mb-2 block text-sm font-medium text-gray-300">Ticker</label>
        <div className="relative">
          <Combobox
            as="div"
            value={selectedTicker}
            onChange={(newTicker) => {
              if (newTicker !== selectedTicker) {
                const tickerSymbol = newTicker?.t;
                if (tickerSymbol) {
                  setSelectedTicker(newTicker);
                  router.push(getCalendarPage(screenType, tickerSymbol));
                }
              }
            }}
            className="flex w-full"
          >
            <Combobox.Input
              className="h-9 w-full rounded-l border border-gray-600 bg-palette-black-2 px-4 py-2 text-xs text-white outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
              onChange={(event) => search(event.target.value)}
              placeholder="Search ticker (ex. TSLA)"
              autoComplete="off"
              displayValue={(item: TickerType) => item?.t || ''}
            />
            <button
              type="button"
              className="flex w-12 items-center justify-center rounded-r bg-brand-primary transition-colors hover:bg-blue-700"
            >
              <BiSearch className="text-lg text-white" />
            </button>
            {selectedTicker && (
              <button
                type="button"
                className="absolute right-14 top-1/2 -translate-y-1/2 transform text-lg text-gray-400 hover:text-white"
                onClick={() => {
                  setSelectedTicker(null);
                  router.push(getCalendarPage(screenType));
                }}
              >
                ✕
              </button>
            )}
            {searchResults?.length > 0 && (
              <Combobox.Options className="absolute top-full z-60 mt-1 max-h-60 w-full overflow-auto rounded border border-gray-600 bg-palette-black-1 shadow-lg focus:outline-none">
                {searchResults.map((ticker) => (
                  <Combobox.Option
                    key={ticker.t}
                    value={ticker}
                    className={({ active }) =>
                      classNames(
                        'relative cursor-pointer select-none px-4 py-2',
                        active ? 'bg-brand-primary text-white' : 'text-white hover:bg-gray-700',
                      )
                    }
                  >
                    {({ selected }) => (
                      <div className="flex items-center">
                        <span
                          className={classNames('mr-3 font-semibold', selected ? 'text-white' : '')}
                        >
                          {ticker?.t}
                        </span>
                        <span
                          className={classNames(
                            'truncate text-sm',
                            selected ? 'text-white' : 'text-gray-300',
                          )}
                        >
                          {ticker?.n}
                        </span>
                      </div>
                    )}
                  </Combobox.Option>
                ))}
              </Combobox.Options>
            )}
          </Combobox>
        </div>
      </div>
    );
  };

  const renderDateSection = () => (
    <div className="w-full flex-shrink-0 sm:w-auto sm:min-w-[180px]">
      <div className="mb-2 flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-300">Date</label>
        {(selectedDate || fromDate || toDate) && (
          <div className="ml-2 text-sm text-gray-400">
            {selectedDate ? (
              <span>Showing: {format(selectedDate, 'MM/dd/yyyy')}</span>
            ) : fromDate && toDate && formatDateForAPI ? (
              <span>
                Showing data from: {formatDateForAPI(fromDate)} to {formatDateForAPI(toDate)}
              </span>
            ) : (
              <span>Showing: {selectedTimeframe}</span>
            )}
          </div>
        )}
      </div>
      <div className="relative">
        <DatePicker datePickerType="single" value={dateValue} onChange={handleDateChange}>
          <DatePickerInput
            hideLabel
            placeholder="mm/dd/yyyy"
            labelText=""
            id="start-date-calendar"
          />
        </DatePicker>
        {selectedDate && (
          <button
            type="button"
            className="absolute right-10 top-1/2 -translate-y-1/2 transform text-gray-400 hover:text-white"
            onClick={clearDateSelection}
            title="Clear date selection"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );

  const renderTimeframeButtons = () => (
    <div className=" min-w-0">
      <div className="flex flex-wrap gap-1.5">
        {timeframeOptions.map((label) => (
          <button
            key={label}
            onClick={() => {
              setSelectedTimeframe(label);
              clearDateSelection();
            }}
            className={classNames(
              'whitespace-nowrap rounded border px-3 py-2 text-xs font-medium transition-colors',
              selectedTimeframe === label && !selectedDate
                ? 'bg-white text-brand-primary'
                : 'border-gray-600 text-white hover:border-gray-500 hover:text-gray-300',
            )}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  const renderThirdSection = () => (
    <div className="min-w-0 w-full xl:w-auto">
      <label className="mb-2 flex items-center text-sm font-medium text-gray-300">
        {config.thirdSectionTitle}
        {config.tooltipContent && (
          <div className="group relative ml-2 flex items-center gap-1">
            <MdInfoOutline className="cursor-pointer text-base text-gray-400 transition-colors hover:text-gray-300" />
            <div className="absolute left-1/2 top-[calc(100%+10px)] z-50 hidden -translate-x-1/2 group-hover:block">
              <div className="relative w-56 rounded-lg border border-gray-600 bg-palette-black-4 p-3 text-xs text-white shadow-lg">
                <div className="absolute -top-2 left-1/2 z-10 h-4 w-4 -translate-x-1/2 rotate-45 border-l border-t border-gray-600 bg-gray-800" />
                {config.tooltipContent}
              </div>
            </div>
          </div>
        )}
      </label>
      <div className="flex flex-wrap gap-1.5">
        {config.thirdSectionOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => toggleThirdSectionFilter(option.value)}
            className={classNames(
              'flex h-8 items-center justify-center rounded border text-xs font-medium transition-colors',

              React.isValidElement(option.label) ? 'w-8' : 'min-w-[3rem] px-3',
              selectedThirdSectionFilters.has(option.value)
                ? 'bg-white text-brand-primary'
                : 'border-gray-600 text-gray-400 hover:border-gray-500 hover:text-gray-300',
            )}
          >
            {option.value === 'DM' ? (
              <IoMdClock
                className={classNames(
                  'text-lg',
                  selectedThirdSectionFilters.has(option.value) ? 'text-black' : 'text-white',
                )}
              />
            ) : (
              option.label
            )}
          </button>
        ))}
      </div>
    </div>
  );

  const renderFourthSection = () => {
    if (
      !config.fourthSectionOptions ||
      !selectedFourthSectionFilters ||
      !toggleFourthSectionFilter
    ) {
      return null;
    }

    return (
      <div className="min-w-0 w-full xl:w-auto">
        <label className="mb-2 block text-sm font-medium text-gray-300">
          {config.fourthSectionTitle}
        </label>
        <div className="flex flex-wrap gap-1.5">
          {config.fourthSectionOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => toggleFourthSectionFilter(option.value)}
              className={classNames(
                'flex h-8 items-center justify-center rounded border px-3 text-xs font-medium transition-colors',
                selectedFourthSectionFilters.has(option.value)
                  ? 'bg-white text-brand-primary'
                  : 'border-gray-600 text-gray-400 hover:border-gray-500 hover:text-gray-300',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderTableHeader = () => {
    if (resultsCount === undefined) return null;

    return (
      <div className="container mx-auto max-w-full px-4 py-2">
        <div className="rounded-t-lg border border-b-0 border-brand-green-primary bg-palette-black-4 px-4 py-3">
          <h2 className="text-lg font-medium text-white">
            Results {resultsCount !== undefined && `(${resultsCount.toLocaleString()})`}
          </h2>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="border-1 container mx-auto max-w-full rounded-lg border-brand-green-primary bg-palette-black-4">
        <div className="p-4">
          <div className="flex flex-wrap items-end gap-2 xl:flex-nowrap">
            {config.hasTickerSearch &&
              setSelectedTicker &&
              setSearchResults &&
              renderTickerSearch()}
            {renderDateSection()}
            {renderTimeframeButtons()}
            {renderThirdSection()}
            {renderFourthSection()}
          </div>
        </div>
      </div>
      {renderTableHeader()}
    </>
  );
};
